"use client";

import { useState, useMemo } from "react";
import { Button, Card } from "@repo/ui";
import { getNextCohort } from "@repo/constants/between-sessions";
import {
  ArrowRight,
  CheckCircle,
  Star,
  Users,
  Heart,
  BookOpen,
  MessageCircle,
  GraduationCap,
  Clock,
  Sparkles,
  ChevronDown,
  Calendar,
  Lightbulb,
  HandHeart,
  Rocket,
  HelpCircle,
} from "lucide-react";

const testimonials = [
  {
    quote: "Having a community of fellow interns made all the difference. I didn't feel alone during the tough moments of practicum.",
    name: "Sarah M.",
    program: "Yorkville University",
    highlight: "Core Member",
    gradient: "from-violet-500 to-purple-600",
    image: "SM",
  },
  {
    quote: "The group supervision sessions helped me work through cases I was stuck on. Worth every penny.",
    name: "James L.",
    program: "Adler University",
    highlight: "Community Member",
    gradient: "from-rose-500 to-pink-600",
    image: "JL",
  },
  {
    quote: "I loved being matched with a peer group that stayed together all trimester. We still keep in touch!",
    name: "Maya R.",
    program: "City University",
    highlight: "Community Member",
    gradient: "from-blue-500 to-indigo-600",
    image: "MR",
  },
  {
    quote: "The resource library and hours tracking tools saved me so much time. Super practical for busy students.",
    name: "David K.",
    program: "Tyndale University",
    highlight: "Core Member",
    gradient: "from-emerald-500 to-teal-600",
    image: "DK",
  },
];

const faqs = [
  {
    question: "I already have a placement. Can I still join?",
    answer: "Absolutely! Between Sessions is designed for students doing practicum anywhere. You get community support, resources, and optional supervision while completing your placement at another site.",
    icon: HelpCircle,
  },
  {
    question: "What's the difference between Core and Community?",
    answer: "Core gives you access to our Slack community, resource library, hours tracking, and monthly knowledge shares. Community includes everything in Core plus monthly group supervision and matched peer groups that meet regularly.",
    icon: Users,
  },
  {
    question: "How does the student pricing work?",
    answer: "Students get special discounted rates: $45/month for Core (regularly $75) and $95/month for Community (regularly $125). The student rate requires an 8-month commitment instead of 4 months.",
    icon: GraduationCap,
  },
  {
    question: "When do cohorts start?",
    answer: "New cohorts begin three times per year: January, May, and September. You'll be matched with other students at a similar stage in their training.",
    icon: Calendar,
  },
  {
    question: "Can I upgrade from Core to Community later?",
    answer: "Yes! You can upgrade at any time. We'll prorate the difference and get you set up with your peer group and supervision schedule.",
    icon: Rocket,
  },
];

export default function StudentCommunityPage() {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  // Get next cohort intake date
  const nextCohort = useMemo(() => getNextCohort(), []);

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-20 pb-24 px-4 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-violet-50 via-rose-50 to-purple-50" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-gradient-to-br from-rose-200 to-pink-300 rounded-full opacity-20 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-br from-violet-200 to-purple-300 rounded-full opacity-20 blur-3xl" />

        <div className="relative max-w-5xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm mb-8">
            <Heart className="w-4 h-4 text-rose-500" />
            <span className="text-gray-700 font-medium text-sm">For Practicum Students</span>
          </div>

          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Your Practicum.<br />
            <span className="bg-gradient-to-r from-violet-600 to-rose-500 bg-clip-text text-transparent">
              Our Community.
            </span>
          </h1>

          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Doing your practicum elsewhere? Get the peer support, resources, and supervision you need to thrive—without changing your placement.
          </p>

          {/* Cohort info */}
          <div className="inline-flex items-center gap-2 bg-violet-100 px-4 py-2 rounded-full mb-8">
            <Sparkles className="w-4 h-4 text-violet-600" />
            <span className="text-violet-700 font-medium text-sm">
              Now accepting for <strong>{nextCohort.name}</strong> starting {nextCohort.displayDate}
            </span>
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto mt-12">
            {[
              { icon: Users, stat: "100+", label: "Active student members" },
              { icon: GraduationCap, stat: "9+", label: "Partner schools" },
              { icon: MessageCircle, stat: "Daily", label: "Peer support" },
            ].map((item, i) => (
              <div key={i} className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-sm">
                <item.icon className="w-8 h-8 text-violet-500 mx-auto mb-3" />
                <div className="text-2xl font-bold text-gray-900">{item.stat}</div>
                <div className="text-sm text-gray-500">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* School Logos */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-gray-500 text-sm">
              Students from leading counselling and psychology programs
            </p>
          </div>

          {/* School Logos Marquee */}
          <div className="relative overflow-hidden">
            <div className="flex animate-marquee">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((num, idx) => (
                <div
                  key={idx}
                  className="flex-shrink-0 mx-8 md:mx-12 grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all duration-300"
                >
                  <img
                    src={`/school_${num}.png`}
                    alt={`Partner school ${num}`}
                    className="h-16 md:h-20 w-auto object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Join Section */}
      <section className="py-24 px-4 bg-gradient-to-b from-white to-violet-50/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-violet-600 font-semibold text-sm uppercase tracking-wider">Why Join?</span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mt-4 mb-6">
              Practicum is hard.<br />You don't have to do it alone.
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get the support your program can't always provide—peer connection, practical resources, and guided supervision from clinicians who get it.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Users,
                title: "Daily Peer Support",
                description: "Connect with fellow interns in our private Slack. Share wins, ask questions, and find your people.",
                color: "violet",
              },
              {
                icon: BookOpen,
                title: "Practical Resources",
                description: "Access clinical tools, templates, and our full resource library. Plus hours tracking to stay organized.",
                color: "rose",
              },
              {
                icon: Lightbulb,
                title: "Monthly Learning",
                description: "Knowledge shares and LevelUp Labs curriculum to build the skills your textbooks don't teach.",
                color: "amber",
              },
            ].map((item, i) => (
              <Card key={i} className="p-8 text-center hover:shadow-xl transition-shadow">
                <div className={`w-16 h-16 bg-${item.color}-100 rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                  <item.icon className={`w-8 h-8 text-${item.color}-500`} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Features Section */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-violet-600 font-semibold text-sm uppercase tracking-wider">What You Get</span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mt-4 mb-6">
              Everything Included
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Both tiers include powerful resources. Community adds supervision and deeper peer connection.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Core Features */}
            <div className="bg-violet-50 rounded-3xl p-8 border border-violet-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-violet-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Core Features</h3>
                  <p className="text-sm text-violet-600">Included in both tiers</p>
                </div>
              </div>

              <div className="space-y-4">
                {[
                  {
                    icon: MessageCircle,
                    title: "Private Slack Community",
                    desc: "24/7 access to fellow interns. Share resources, ask questions, celebrate wins.",
                  },
                  {
                    icon: BookOpen,
                    title: "Full Resource Library",
                    desc: "Clinical templates, assessment tools, and practical guides curated for interns.",
                  },
                  {
                    icon: Clock,
                    title: "Hours Tracking Tools",
                    desc: "Stay organized with our tracking tools designed for practicum requirements.",
                  },
                  {
                    icon: Lightbulb,
                    title: "Monthly Knowledge Shares",
                    desc: "Learn from experts on topics like ethics, documentation, and clinical skills.",
                  },
                  {
                    icon: GraduationCap,
                    title: "LevelUp Labs Curriculum",
                    desc: "Monthly skills-building content that goes beyond what textbooks teach.",
                  },
                  {
                    icon: Star,
                    title: "Member Discounts",
                    desc: "Save on liability insurance, continuing education, and partner resources.",
                  },
                ].map((feature, i) => (
                  <div key={i} className="flex items-start gap-4 bg-white rounded-xl p-4">
                    <feature.icon className="w-5 h-5 text-violet-500 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">{feature.title}</h4>
                      <p className="text-sm text-gray-600">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Community Extras */}
            <div className="bg-rose-50 rounded-3xl p-8 border border-rose-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center">
                  <Heart className="w-6 h-6 text-rose-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Community Extras</h3>
                  <p className="text-sm text-rose-600">Added with Community tier</p>
                </div>
              </div>

              <div className="space-y-4">
                {[
                  {
                    icon: Users,
                    title: "Monthly Group Supervision",
                    desc: "90-minute sessions with up to 8 therapists, led by licensed clinicians. Process cases, get feedback, grow.",
                  },
                  {
                    icon: HandHeart,
                    title: "Monthly Peer Support Group",
                    desc: "Facilitated processing space to discuss the emotional side of clinical work.",
                  },
                  {
                    icon: Heart,
                    title: "Matched Peer Group",
                    desc: "You're placed in a cohort that stays together. Build real relationships that last.",
                  },
                  {
                    icon: Calendar,
                    title: "À La Carte Individual Supervision",
                    desc: "Need one-on-one support? Access individual sessions at member rates.",
                  },
                ].map((feature, i) => (
                  <div key={i} className="flex items-start gap-4 bg-white rounded-xl p-4">
                    <feature.icon className="w-5 h-5 text-rose-500 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">{feature.title}</h4>
                      <p className="text-sm text-gray-600">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Upgrade note */}
              <div className="mt-6 bg-rose-100 rounded-xl p-4 text-center">
                <p className="text-rose-700 text-sm font-medium">
                  Start with Core and upgrade anytime—we'll prorate the difference.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 px-4 bg-gradient-to-b from-violet-50/30 to-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-violet-600 font-semibold text-sm uppercase tracking-wider">Choose Your Path</span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mt-4 mb-6">
              Student Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Special rates for practicum students. Pick the level of support that fits your needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Core Tier */}
            <Card
              className="p-8 relative overflow-hidden border-2 border-violet-200 hover:border-violet-400 hover:shadow-xl transition-all cursor-pointer group"
              onClick={() => window.location.href = '/between-sessions/join?tier=core&student=true'}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-violet-400 to-purple-400 rounded-full opacity-10 -mr-16 -mt-16 group-hover:opacity-20 transition-opacity" />
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 bg-violet-100 px-3 py-1 rounded-full mb-4">
                  <Users className="w-4 h-4 text-violet-600" />
                  <span className="text-violet-700 font-medium text-sm">Core</span>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg text-gray-400 line-through">$75</span>
                    <span className="text-4xl font-bold text-gray-900">$45</span>
                    <span className="text-gray-500">/month</span>
                  </div>
                  <p className="text-sm text-violet-600 font-medium mt-1">Student rate</p>
                  <p className="text-xs text-gray-500 mt-1">8-month minimum commitment</p>
                </div>

                <p className="text-gray-600 mb-6">
                  Community, resources, and tools to support you through practicum.
                </p>

                <ul className="space-y-3 mb-8">
                  {[
                    "Private Slack community",
                    "Full resource library & clinical tools",
                    "Hours tracking tools",
                    "Monthly knowledge share sessions",
                    "Monthly LevelUp Labs curriculum",
                    "Member discounts on insurance & CE",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-violet-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  size="lg"
                  className="w-full bg-violet-600 hover:bg-violet-700 text-white"
                  onClick={() => window.location.href = '/between-sessions/join?tier=core&student=true'}
                >
                  Join Core
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </Card>

            {/* Community Tier */}
            <Card
              className="p-8 relative overflow-hidden border-2 border-rose-200 hover:border-rose-400 hover:shadow-xl transition-all cursor-pointer group"
              onClick={() => window.location.href = '/between-sessions/join?tier=community&student=true'}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-rose-400 to-pink-400 rounded-full opacity-10 -mr-16 -mt-16 group-hover:opacity-20 transition-opacity" />

              {/* Popular badge */}
              <div className="absolute top-4 right-4">
                <div className="inline-flex items-center gap-1 bg-rose-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                  <Sparkles className="w-3 h-3" />
                  Popular
                </div>
              </div>

              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 bg-rose-100 px-3 py-1 rounded-full mb-4">
                  <Heart className="w-4 h-4 text-rose-600" />
                  <span className="text-rose-700 font-medium text-sm">Community</span>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg text-gray-400 line-through">$125</span>
                    <span className="text-4xl font-bold text-gray-900">$95</span>
                    <span className="text-gray-500">/month</span>
                  </div>
                  <p className="text-sm text-rose-600 font-medium mt-1">Student/alumni rate</p>
                  <p className="text-xs text-gray-500 mt-1">8-month minimum commitment</p>
                </div>

                <p className="text-gray-600 mb-6">
                  Everything in Core plus group supervision and matched peer groups.
                </p>

                {/* Core features included */}
                <div className="bg-violet-50 rounded-xl p-4 mb-4 border border-violet-100">
                  <p className="text-violet-800 font-medium text-sm mb-2">Everything in Core, plus:</p>
                </div>

                <ul className="space-y-3 mb-8">
                  {[
                    "Monthly group supervision (90 min, up to 8)",
                    "Monthly peer support group",
                    "Matched peer group that stays together",
                    "À la carte individual supervision access",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  size="lg"
                  className="w-full bg-rose-500 hover:bg-rose-600 text-white"
                  onClick={() => window.location.href = '/between-sessions/join?tier=community&student=true'}
                >
                  Join Community
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </Card>
          </div>

          {/* Note about commitment */}
          <p className="text-center text-gray-500 text-sm mt-8 max-w-2xl mx-auto">
            Student rates require an 8-month commitment (vs. 4 months at regular price).
            This aligns with the academic year and ensures you have support throughout your practicum.
          </p>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-4 bg-gradient-to-b from-white to-violet-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-violet-600 font-semibold text-sm uppercase tracking-wider">Student Stories</span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mt-4 mb-6">
              Hear From Our Members
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="p-6 hover:shadow-xl transition-all">
                <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${testimonial.gradient}`} />

                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>

                <blockquote className="text-gray-700 mb-6 italic text-sm leading-relaxed">
                  "{testimonial.quote}"
                </blockquote>

                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 bg-gradient-to-br ${testimonial.gradient} rounded-full flex items-center justify-center text-white font-bold text-sm`}>
                    {testimonial.image}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{testimonial.name}</p>
                    <p className="text-xs text-gray-500">{testimonial.program}</p>
                    <p className="text-xs font-medium text-violet-600">{testimonial.highlight}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-4 bg-violet-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-violet-600 font-semibold text-sm uppercase tracking-wider">FAQ</span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mt-4 mb-6">
              Common Questions
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-2xl hover:border-violet-300 transition-colors overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-violet-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <faq.icon className="w-5 h-5 text-violet-600" />
                    <h3 className="text-lg font-semibold text-gray-900">{faq.question}</h3>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-violet-600 transition-transform ${expandedFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-600 pl-9">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-4 bg-gradient-to-br from-violet-600 to-rose-500">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Ready to join your community?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            {nextCohort.name} cohort starts {nextCohort.displayDate}. Join now and get matched with your peer group.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-violet-600 hover:bg-gray-100 px-8 py-6 text-lg font-semibold"
              onClick={() => window.location.href = '/between-sessions/join?tier=community&student=true'}
            >
              Join Community — $95/mo
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold"
              onClick={() => window.location.href = '/between-sessions/join?tier=core&student=true'}
            >
              Join Core — $45/mo
            </Button>
          </div>
        </div>
      </section>

      {/* Styles for animations */}
      <style jsx>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }

        .animate-marquee {
          animation: marquee 30s linear infinite;
        }

        .animate-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
}
